// noinspection DuplicatedCode

import {EventFromTarget} from './@types';
import {Runner, RunnerInterface} from './models/runner';
import SearchProvider from './providers/search';
import RunnersProvider from './providers/runners';
import RunProvider from './providers/run';
import RenderProvider from './providers/render';
import initSearchFilters, {SearchFiltersStore} from './store/searchFilters';
import initSearchResults, {SearchResultsStore} from './store/searchResults';
import initRunnersStore, {RunnersStore} from './store/runners';
import Tracking from './tracking';

export default class Search {
    year: number;

    private searchProvider: SearchProvider;

    private runnersProvider: RunnersProvider;

    private runProvider: RunProvider;

    private renderProvider: RenderProvider;

    private searchResults: SearchResultsStore;

    private searchFilters: SearchFiltersStore;

    private favoriteRunners: RunnersStore;

    constructor() {
        this.searchProvider = SearchProvider.getInstance();
        this.runnersProvider = RunnersProvider.getInstance();
        this.runProvider = RunProvider.getInstance();
        this.renderProvider = RenderProvider.getInstance();
        this.searchResults = initSearchResults();
        this.searchFilters = initSearchFilters();
        this.favoriteRunners = initRunnersStore();
    }

    init() {
        this.year = this.runProvider.getLatestYear();
        const {run} = this.favoriteRunners.getState();

        if (run) {
            this.searchFilters.setRun(run);
        }

        this.favoriteRunners.subscribe(({run: currentRun}) => {
            this.searchFilters.setRun(currentRun);
        });

        this.renderProvider.searchToggleButton?.addEventListener('click', () => {
            this.initSearch();
        });
    }

    initSearch() {
        this.renderProvider.showLoader();

        this.runnersProvider.fetchRunnersList(this.year)
            .then(() => {
                const {
                    run,
                    query,
                } = this.searchFilters.getState();
                this.setFilteredItems(run, query);
                this.renderSearchResults();
            });

        this.renderProvider.showSearchContext();

        this.renderProvider.searchInput.addEventListener('input', ({target}: EventFromTarget<HTMLInputElement>) => {
            this.searchFilters.setQuery(target.value);
        });

        this.favoriteRunners.subscribe(({run}) => {
            this.searchFilters.setRun(run);
        });

        this.searchFilters.subscribe(({
            run,
            query,
        }) => {
            this.setFilteredItems(run, query);
            this.renderSearchResults();
        });
    }

    setFilteredItems(run: string, query: string) {
        const runners = this.searchProvider
            .filterRunners(this.year, query, run)
            .slice(0, 25);
        this.searchResults.setRunners(runners);
    }

    isFavorite(runnerInterface: RunnerInterface): boolean {
        const {favoriteRunnerInterfaces} = this.favoriteRunners.getState();

        return runnerInterface && favoriteRunnerInterfaces.find(({
            year,
            startNumber,
        }) => {
            return runnerInterface.startNumber === startNumber && runnerInterface.year === year;
        }) !== undefined;
    }

    removeFavoriteRunner(runnerInterface: RunnerInterface) {
        const {
            favoriteRunners,
            setFavoriteRunners,
        } = this.favoriteRunners.getState();

        if (this.isFavorite(runnerInterface)) {
            setFavoriteRunners(favoriteRunners.filter(
                ({startNumber}) => startNumber !== runnerInterface.startNumber,
            ));
        }
    }

    async addFavoriteRunner(runnerInterface: RunnerInterface) {
        const {
            favoriteRunners,
            setFavoriteRunners,
        } = this.favoriteRunners.getState();

        if (Tracking.hasReachedLimit(favoriteRunners)) return;

        if (!this.isFavorite(runnerInterface)) {
            setFavoriteRunners([
                ...favoriteRunners,
                new Runner(runnerInterface),
            ]);
            await this.runnersProvider.fetchUpdateRunners(favoriteRunners);
        }
    }

    async toggleRunner(runnerInterface: RunnerInterface) {
        if (this.isFavorite(runnerInterface)) {
            this.removeFavoriteRunner(runnerInterface);
        } else {
            await this.addFavoriteRunner(runnerInterface);
        }
    }

    async selectRunner(startNumber: string) {
        const {runners} = this.searchResults.getState();
        const selectedRunnerInterface = runners.find((runner) => runner.startNumber === startNumber);

        if (selectedRunnerInterface) {
            await this.toggleRunner(selectedRunnerInterface);
            this.renderProvider.searchInput.value = '';
        }
    }

    renderSearchResults() {
        const {favoriteRunners} = this.favoriteRunners.getState();
        if (Tracking.hasReachedLimit(favoriteRunners)) {
            this.renderProvider.showWarningLimitReached();
            return;
        }

        const {runners} = this.searchResults.getState();
        this.renderProvider.renderSearchResultsDropdown(
            runners,
            async (startNumber: string) => {
                await this.selectRunner(startNumber);
            },
            this.isFavorite.bind(this),
        );

        this.renderProvider.hideLoader();
    }
}
